import { TASK_STATUSES, type Label, type Member, type TaskStatus } from '@hive/shared';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import type { GridTask } from '@/data/tasks';
import type { Columns } from './model';
import { PriorityMark, TaskMeta } from './TaskBits';

const PAGE = 50;

/** Grid's list view: one section per status. Done starts folded. */
export function TaskList({
  columns,
  labels,
  members,
  subtasks,
  today,
  onOpen,
  onToggleDone,
}: {
  columns: Columns;
  labels: readonly Label[];
  members: readonly Member[];
  subtasks: Map<string, GridTask[]>;
  today: string;
  onOpen: (taskId: string) => void;
  onToggleDone: (task: GridTask) => void;
}) {
  const [folded, setFolded] = useState<Partial<Record<TaskStatus, boolean>>>({ done: true });
  const [shown, setShown] = useState<Partial<Record<TaskStatus, number>>>({});

  return (
    <div className="flex flex-col gap-5">
      {TASK_STATUSES.map((status) => {
        const tasks = columns[status.id];
        const isFolded = folded[status.id] ?? false;
        const limit = shown[status.id] ?? PAGE;
        const Chevron = isFolded ? ChevronRight : ChevronDown;
        return (
          <section key={status.id} aria-label={status.name}>
            <h2>
              <button
                type="button"
                className="flex cursor-pointer items-center gap-2 font-display text-[11px] font-bold tracking-[0.16em] text-cyan uppercase"
                aria-expanded={!isFolded}
                onClick={() => setFolded((f) => ({ ...f, [status.id]: !isFolded }))}
              >
                <Chevron className="size-3.5" aria-hidden />
                {status.name}
                <span className="font-mono text-muted">{tasks.length}</span>
              </button>
            </h2>
            {isFolded ? null : (
              <ul className="mt-2 flex flex-col gap-px">
                {tasks.length === 0 ? <li className="px-3 py-2 text-sm text-dim">Nothing here.</li> : null}
                {tasks.slice(0, limit).map((task) => (
                  <li
                    key={task.id}
                    className="group flex items-center gap-3 bg-bg-2/70 px-3 py-2 shadow-[inset_0_0_0_1px_var(--color-line)] hover:shadow-[inset_0_0_0_1px_var(--color-line-hot)]"
                  >
                    <input
                      type="checkbox"
                      className="size-4 shrink-0 cursor-pointer accent-cyan"
                      checked={task.status === 'done'}
                      onChange={() => onToggleDone(task)}
                      aria-label={task.status === 'done' ? `Reopen ${task.title}` : `Complete ${task.title}`}
                    />
                    <PriorityMark priority={task.priority} />
                    <button
                      type="button"
                      className={cn(
                        'min-w-0 flex-1 cursor-pointer truncate text-left font-semibold hover:text-cyan',
                        task.status === 'done' && 'text-muted line-through',
                      )}
                      onClick={() => onOpen(task.id)}
                    >
                      {task.title}
                    </button>
                    <span className="flex shrink-0 items-center gap-2">
                      <TaskMeta
                        task={task}
                        labels={labels}
                        members={members}
                        subtaskCount={subtasks.get(task.id)?.length ?? 0}
                        today={today}
                      />
                    </span>
                  </li>
                ))}
                {tasks.length > limit ? (
                  <li>
                    <button
                      type="button"
                      className="cursor-pointer px-3 py-2 font-mono text-xs text-cyan hover:underline"
                      onClick={() => setShown((s) => ({ ...s, [status.id]: limit + PAGE * 4 }))}
                    >
                      Show more ({tasks.length - limit} left)
                    </button>
                  </li>
                ) : null}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
